import { Injectable, inject } from '@angular/core';

import { EventModel } from '@frontend/sports/betting-offer/feature/model';
import { bufferWithSize } from '@frontend/sports/common/core/utils/rxjs';
import { FreshDataService } from '@frontend/sports/data-refresh/feature';
import { EventCallback, EventSubscription } from '@frontend/sports/event-subscription/feature/base-subscription';
import { EventSubscriptionService } from '@frontend/sports/event-subscription/feature/event-subscription';
import { SportsProductUtils } from '@frontend/sports/host-app/sports-product/feature/utils';
import { Observable, Subject, asyncScheduler, debounceTime, distinct, filter, map, noop, throttleTime } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class LiveEventsSubscriptionService {
    private freshdataService = inject(FreshDataService);
    private eventSubscriptionService = inject(EventSubscriptionService);

    private subscriptions = new Map<string, EventSubscription<EventModel>>();
    private eventsStream$ = new Subject<EventModel>();
    readonly updatedEvents$: Observable<EventModel[]>;

    constructor() {
        const productUtils = inject(SportsProductUtils);

        const updateQueueInMilliseconds = 400;
        const maxUpdateQueueCount = 10;

        // buffer updates and notify at once for performance reasons
        const flush$ = this.eventsStream$.pipe(map(noop), throttleTime(updateQueueInMilliseconds, asyncScheduler));
        const debounceTimer = this.eventsStream$.pipe(debounceTime(updateQueueInMilliseconds));

        this.updatedEvents$ = this.eventsStream$.pipe(
            distinct((event) => event.id, flush$),
            bufferWithSize(maxUpdateQueueCount, debounceTimer),
            filter((events) => events && events.length > 0),
        );

        this.freshdataService.reloadNeeded.pipe(productUtils.filterSportsEmitLast()).subscribe(() => this.unsubscribeAll());
    }

    isSubscribed(eventId: string): boolean {
        return this.subscriptions.has(eventId);
    }

    subscribe(model: EventModel): void {
        if (!this.subscriptions.get(model.id)) {
            const subscription = this.subscribeEvent(model);
            if (subscription) {
                this.subscriptions.set(model.id, subscription);
            }
        }
    }

    unsubscribe(fixtureId: string | string[]): void {
        const ids = Array.isArray(fixtureId) ? fixtureId : [fixtureId];
        this.unsubscribeEvent(ids);
    }

    unsubscribeAll(): void {
        this.subscriptions.forEach((s) => s.unsubscribe());
        this.subscriptions.clear();
    }

    private unsubscribeEvent(ids: string[]): void {
        ids.forEach((id) => {
            const subscription = this.subscriptions.get(id);
            if (subscription) {
                subscription.unsubscribe();
                this.subscriptions.delete(id);
            }
        });
    }

    private subscribeEvent(event: EventModel): EventSubscription<EventModel> {
        const callback: EventCallback<EventModel> = (data) => {
            const subscription = this.subscriptions.get(data.id);
            if (subscription) {
                this.eventsStream$.next(data);
            }
        };

        return this.eventSubscriptionService.subscribe([{ event, callback }]);
    }
}
